import { useCallback, useMemo, useState } from 'react';
import PageHeader from '../../../shared/components/PageHeader';
import CompanyPeriodSelector from '../../../shared/components/CompanyPeriodSelector';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import { useCompanyWriteAccess } from '../../../shared/auth/useCompanyWriteAccess';
import './PnlClosingPage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE_URL = baseEnvironment.replace(/\/$/, '');

const UI_TEXT = {
  'tr-TR': {
    title: 'Gelir Tablosu Hesaplari Donem Sonu Kontrolu ve Duzeltmeler',
    description: '7xx gider hesaplarini yansitma hesaplarina (7x1), oradan 6xx sonuc hesaplarina aktarir. trial_balance_entries degistirilmez.',
    runAnalysis: 'Donem Sonu Yansitma Analizi Calistir',
    running: 'Analiz yapiliyor...',
    apply: 'Fisleri Olustur ve Uygula ({{count}} transfer)',
    applying: 'Uygulaniyor...',
    rollback: 'Geri Al',
    rollbackConfirm: 'Gelir tablosu donem sonu fisleri geri alinacak. Devam edilsin mi?',
    analysisFailed: 'Analiz basarisiz.',
    analysisError: 'Analiz sirasinda hata olustu.',
    applyFailed: 'Fisler olusturulamadi.',
    applyError: 'Fis olusturma sirasinda hata olustu.',
    rollbackFailed: 'Geri alma basarisiz.',
    rollbackError: 'Geri alma sirasinda hata olustu.',
    applySuccess: '{{voucherCount}} fis olusturuldu, {{lineCount}} satir. Kebir mizan yeniden hesaplandi.',
    summaryTotal: 'Toplam transfer',
    summaryAuto: 'Otomatik',
    summaryManual: 'Manuel',
    findingsTitle: 'Bulgu Listesi',
    colSourceCode: 'Kaynak Hesap',
    colSourceName: 'Kaynak Adi',
    colTargetCode: 'Hedef Hesap',
    colTargetName: 'Hedef Adi',
    colNet: 'Net Tutar',
    colVoucherGroup: 'Fis Grubu',
    colStatus: 'Durum',
    colDescription: 'Aciklama',
    auto: 'Otomatik',
    manual: 'Manuel',
    empty: 'Yansitilacak bakiye tespit edilmedi.',
    readOnlyAccess: 'Bu sirket icin yazma yetkiniz yok.'
  },
  'en-US': {
    title: 'P&L Closing Control and Adjustments',
    description: 'Transfers 7xx expense accounts to allocation accounts (7x1), then to 6xx result accounts. trial_balance_entries are not modified.',
    runAnalysis: 'Run Period-End Allocation Analysis',
    running: 'Running analysis...',
    apply: 'Create and Apply Vouchers ({{count}} transfers)',
    applying: 'Applying...',
    rollback: 'Rollback',
    rollbackConfirm: 'P&L period-end vouchers will be rolled back. Continue?',
    analysisFailed: 'Analysis failed.',
    analysisError: 'An error occurred during analysis.',
    applyFailed: 'Vouchers could not be created.',
    applyError: 'An error occurred while creating vouchers.',
    rollbackFailed: 'Rollback failed.',
    rollbackError: 'An error occurred during rollback.',
    applySuccess: '{{voucherCount}} vouchers created, {{lineCount}} lines. Trial balance recalculated.',
    summaryTotal: 'Total transfers',
    summaryAuto: 'Auto',
    summaryManual: 'Manual',
    findingsTitle: 'Findings',
    colSourceCode: 'Source Account',
    colSourceName: 'Source Name',
    colTargetCode: 'Target Account',
    colTargetName: 'Target Name',
    colNet: 'Net Amount',
    colVoucherGroup: 'Voucher Group',
    colStatus: 'Status',
    colDescription: 'Description',
    auto: 'Auto',
    manual: 'Manual',
    empty: 'No balance found to allocate.',
    readOnlyAccess: 'You do not have write access for this company.'
  }
};

const resolveLocaleKey = (value) => (String(value || '').toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR');

const buildHeaders = () => {
  const token = sessionStorage.getItem('authToken');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

function PnlClosingPage() {
  const { language } = useLanguage();
  const localeKey = resolveLocaleKey(language);
  const text = UI_TEXT[localeKey];
  const t = useCallback((key) => text[key] || UI_TEXT['tr-TR'][key] || key, [text]);

  const currencyFormatter = useMemo(() => new Intl.NumberFormat(localeKey, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }), [localeKey]);

  const formatCurrency = useCallback((value) => {
    if (value == null || Number.isNaN(Number(value))) return currencyFormatter.format(0);
    return currencyFormatter.format(Number(value));
  }, [currencyFormatter]);

  const [selection, setSelection] = useState({ companyId: '', periodId: '', company: null, period: null });
  const [analysis, setAnalysis] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const { canWrite } = useCompanyWriteAccess(selection.companyId);

  const runAnalysis = useCallback(async () => {
    if (!selection.companyId || !selection.periodId) return;
    setLoading(true);
    setError('');
    setApplyResult(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/financials/pnl-closing/analyze?companyId=${selection.companyId}&periodId=${selection.periodId}`, { headers: buildHeaders() });
      if (!res.ok) {
        const textError = await res.text();
        throw new Error(textError || t('analysisFailed'));
      }
      const data = await res.json();
      setAnalysis(data);
    } catch (e) {
      setError(e?.message || t('analysisError'));
      setAnalysis(null);
    } finally {
      setLoading(false);
    }
  }, [selection.companyId, selection.periodId, t]);

  const handleApply = useCallback(async () => {
    if (!canWrite) {
      setError(t('readOnlyAccess'));
      return;
    }
    if (!selection.companyId || !selection.periodId) return;
    setProcessing(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/financials/pnl-closing/apply?companyId=${selection.companyId}&periodId=${selection.periodId}`, { method: 'POST', headers: buildHeaders() });
      if (!res.ok) {
        let msg = t('applyFailed');
        try {
          const json = await res.json();
          if (json?.message) msg = json.message;
        } catch {
          const textError = await res.text();
          if (textError) msg = textError;
        }
        throw new Error(msg);
      }
      const data = await res.json();
      setApplyResult(data);
      runAnalysis();
    } catch (e) {
      setError(e?.message || t('applyError'));
    } finally {
      setProcessing(false);
    }
  }, [canWrite, selection.companyId, selection.periodId, runAnalysis, t]);

  const handleRollback = useCallback(async () => {
    if (!canWrite) {
      setError(t('readOnlyAccess'));
      return;
    }
    if (!selection.companyId || !selection.periodId) return;
    if (!window.confirm(t('rollbackConfirm'))) return;
    setProcessing(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/financials/pnl-closing/rollback?companyId=${selection.companyId}&periodId=${selection.periodId}`, { method: 'POST', headers: buildHeaders() });
      if (!res.ok) throw new Error(t('rollbackFailed'));
      setApplyResult(null);
      runAnalysis();
    } catch (e) {
      setError(e?.message || t('rollbackError'));
    } finally {
      setProcessing(false);
    }
  }, [canWrite, selection.companyId, selection.periodId, runAnalysis, t]);

  const findings = analysis?.findings ?? [];
  const autoFindings = findings.filter((f) => f.status === 'AUTO');

  return (
    <div className="pnl-closing">
      <PageHeader title={t('title')} description={t('description')} />

      <CompanyPeriodSelector onSelectionChange={setSelection} />

      <section className="pnl-closing__actions">
        <button type="button" className="btn-primary" onClick={runAnalysis} disabled={!selection.companyId || !selection.periodId || loading}>
          {loading ? t('running') : t('runAnalysis')}
        </button>
        {autoFindings.length > 0 && (
          <button type="button" className="btn-primary" onClick={handleApply} disabled={!canWrite || processing}>
            {processing ? t('applying') : t('apply').replace('{{count}}', String(autoFindings.length))}
          </button>
        )}
        {(applyResult?.vouchersCreated > 0 || (analysis?.activeCorrectionVoucherCount ?? 0) > 0) && (
          <button type="button" className="btn-secondary" onClick={handleRollback} disabled={!canWrite || processing}>{t('rollback')}</button>
        )}
      </section>

      {error && <p className="pnl-closing__error">{error}</p>}

      {applyResult && applyResult.vouchersCreated > 0 && (
        <div className="pnl-closing__success">
          {t('applySuccess')
            .replace('{{voucherCount}}', String(applyResult.vouchersCreated))
            .replace('{{lineCount}}', String(applyResult.linesCreated))}
        </div>
      )}

      {analysis && (
        <section className="pnl-closing__summary">
          <p>
            <strong>{t('summaryTotal')}:</strong> {analysis.totalCount} | {t('summaryAuto')}: {analysis.autoCount} | {t('summaryManual')}: {analysis.manualCount}
          </p>
        </section>
      )}

      {analysis && findings.length > 0 && (
        <section className="pnl-closing__table-wrapper">
          <h3>{t('findingsTitle')}</h3>
          <table className="pnl-closing__table">
            <thead>
              <tr>
                <th>{t('colSourceCode')}</th>
                <th>{t('colSourceName')}</th>
                <th>{t('colTargetCode')}</th>
                <th>{t('colTargetName')}</th>
                <th className="pnl-closing__align-right">{t('colNet')}</th>
                <th>{t('colVoucherGroup')}</th>
                <th>{t('colStatus')}</th>
                <th>{t('colDescription')}</th>
              </tr>
            </thead>
            <tbody>
              {findings.map((f, i) => (
                <tr key={`${f.sourceAccountCode}-${f.targetAccountCode}-${i}`}>
                  <td>{f.sourceAccountCode}</td>
                  <td>{f.sourceAccountName ?? '-'}</td>
                  <td>{f.targetAccountCode}</td>
                  <td>{f.targetAccountName ?? '-'}</td>
                  <td className="pnl-closing__align-right">{formatCurrency(f.netAmount)}</td>
                  <td>{f.voucherGroup}</td>
                  <td>
                    <span className={f.status === 'AUTO' ? 'pnl-closing__tag ok' : 'pnl-closing__tag manual'}>
                      {f.status === 'AUTO' ? t('auto') : t('manual')}
                    </span>
                  </td>
                  <td>{f.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {analysis && findings.length === 0 && (
        <div className="pnl-closing__empty"><p>{t('empty')}</p></div>
      )}
    </div>
  );
}

export default PnlClosingPage;
